import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  CardActionArea, 
  Chip,
  Stack,
  TextField,
  IconButton,
  InputAdornment,
  Grid,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemButton
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import TagIcon from '@mui/icons-material/Tag';

// Extract keywords from conversations
const extractKeywords = (conversations) => {
  const keywordCount = {};
  
  conversations.forEach(conversation => {
    const content = (conversation.content || '').toLowerCase();
    const title = (conversation.title || '').toLowerCase();
    const text = `${content} ${title}`;
    
    // Simple keyword extraction (words with 3+ characters)
    const words = text.match(/\b\w{3,}\b/g) || [];
    
    words.forEach(word => {
      // Skip common words
      const commonWords = ['the', 'and', 'for', 'are', 'but', 'not', 'you', 'all', 'can', 'had', 'her', 'was', 'one', 'our', 'out', 'day', 'get', 'has', 'him', 'his', 'how', 'its', 'may', 'new', 'now', 'old', 'see', 'two', 'who', 'boy', 'did', 'man', 'way', 'she', 'use', 'her', 'many', 'oil', 'sit', 'set', 'run', 'eat', 'far', 'sea', 'eye', 'ask', 'own', 'say', 'too', 'any', 'try', 'us', 'an', 'as', 'at', 'be', 'he', 'if', 'in', 'is', 'it', 'my', 'of', 'on', 'or', 'to', 'up', 'we', 'go', 'no', 'so', 'am', 'by', 'do', 'me'];
      
      if (!commonWords.includes(word)) {
        keywordCount[word] = (keywordCount[word] || 0) + 1;
      }
    });
  });
  
  // Sort by frequency and return top keywords
  return Object.entries(keywordCount)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 50)
    .map(([keyword, count]) => ({ keyword, count }));
};

// Search panel component
const SearchPanel = ({ searchQuery, onSearch }) => {
  const [keyword, setKeyword] = useState(searchQuery || '');

  const handleKeywordChange = (event) => {
    const value = event.target.value;
    setKeyword(value);
    onSearch(value);
  };

  const clearSearch = () => {
    setKeyword('');
    onSearch('');
  };

  return (
    <Box sx={{ p: 2, backgroundColor: 'background.paper', borderRadius: 1, mb: 2 }}>
      <Typography variant="h6" gutterBottom>
        Search by Keywords
      </Typography>
      
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={8}>
          <TextField
            fullWidth
            label="Search Keywords"
            value={keyword}
            onChange={handleKeywordChange}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: keyword && (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="clear search"
                    onClick={clearSearch}
                    edge="end"
                    size="small"
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <Button
            variant="outlined"
            size="small"
            onClick={clearSearch}
            startIcon={<ClearIcon />}
            fullWidth
          >
            Clear Search
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

// Keyword list component
const KeywordList = ({ keywords, onKeywordClick, searchQuery }) => {
  return (
    <Box sx={{ p: 2, backgroundColor: 'background.paper', borderRadius: 1, mb: 2 }}>
      <Typography variant="h6" gutterBottom>
        Popular Keywords
      </Typography>
      
      {keywords.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No keywords found
        </Typography>
      ) : (
        <List dense>
          {keywords.slice(0, 20).map(({ keyword, count }, index) => (
            <ListItem key={keyword} disablePadding>
              <ListItemButton 
                onClick={() => onKeywordClick(keyword)}
                selected={searchQuery === keyword}
              >
                <TagIcon sx={{ mr: 1, fontSize: 16 }} />
                <ListItemText 
                  primary={keyword}
                  secondary={`${count} occurrence${count !== 1 ? 's' : ''}`}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};

// Search results component
const SearchResults = ({ conversations, searchQuery, onConversationClick, onNavItemClick }) => {
  // Filter conversations based on search query
  const getFilteredConversations = () => {
    if (!searchQuery || !searchQuery.trim()) return [];
    
    const keywords = searchQuery.trim().toLowerCase().split(/\s+/);
    return conversations.filter(conversation => {
      const content = (conversation.content || '').toLowerCase();
      const title = (conversation.title || '').toLowerCase();
      return keywords.some(keyword => 
        content.includes(keyword) || title.includes(keyword)
      );
    }).sort((a, b) => 
      new Date(b.timestamp || 0) - new Date(a.timestamp || 0)
    );
  };

  const filteredConversations = getFilteredConversations();

  if (!searchQuery || !searchQuery.trim()) {
    return null;
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Search Results for "{searchQuery}"
      </Typography>
      
      {filteredConversations.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <SearchIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No conversations found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try different keywords or check the timeline view
          </Typography>
          <Button 
            variant="outlined" 
            sx={{ mt: 2 }}
            onClick={() => onNavItemClick('timeline')}
          >
            View Timeline
          </Button>
        </Box>
      ) : (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {filteredConversations.length} conversation{filteredConversations.length !== 1 ? 's' : ''} found
          </Typography>
          
          {filteredConversations.map((conversation, index) => (
            <Card key={conversation.id || `search-${index}`} sx={{ mb: 2 }} elevation={2}>
              <CardActionArea onClick={() => onConversationClick(conversation)}>
                <CardContent>
                  <Typography variant="h6" component="div" gutterBottom>
                    {conversation.title}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {conversation.content 
                      ? conversation.content.substring(0, 150) + (conversation.content.length > 150 ? '...' : '')
                      : 'No content'
                    }
                  </Typography>

                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip 
                      label={conversation.sender || 'unknown'} 
                      size="small" 
                      color={conversation.sender === 'user' ? 'primary' : 'secondary'}
                      sx={{ mb: 0.5 }}
                    />
                    {conversation.imageData && (
                      <Chip 
                        label="Has Image" 
                        size="small" 
                        color="info"
                        sx={{ mb: 0.5 }}
                      />
                    )}
                  </Stack>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );
};

// Main Keyword View component
const EnKeywordView = ({ 
  conversations, 
  tags,
  onConversationClick, 
  searchQuery, 
  onSearch,
  onNavItemClick
}) => {
  const keywords = extractKeywords(conversations);

  const handleKeywordClick = (keyword) => {
    onSearch(keyword);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom>
        Keywords
      </Typography>
      
      <SearchPanel 
        searchQuery={searchQuery}
        onSearch={onSearch}
      />
      
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <KeywordList 
            keywords={keywords}
            onKeywordClick={handleKeywordClick}
            searchQuery={searchQuery}
          />
        </Grid>
        
        <Grid item xs={12} md={8}>
          <SearchResults 
            conversations={conversations}
            searchQuery={searchQuery}
            onConversationClick={onConversationClick}
            onNavItemClick={onNavItemClick}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default EnKeywordView; 